import React, { useState } from 'react';
import { signInWithRedirect, signIn, signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import 'bootstrap/dist/css/bootstrap.min.css';

const LoginSelection = ({ onAuthSuccess }) => {
  const [loginMode, setLoginMode] = useState('selection'); // 'selection', 'cognito-signin', 'cognito-signup', 'cognito-confirm'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSAMLLogin = async () => {
    try {
      setLoading(true);
      await signInWithRedirect({ provider: 'KONE-AzureAD' });
    } catch (error) {
      console.error('SAML login failed:', error);
      setError('SAML login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleCognitoSignIn = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      const result = await signIn({
        username: formData.username,
        password: formData.password
      });

      if (result.isSignedIn) {
        onAuthSuccess();
      }
    } catch (error) {
      console.error('Cognito sign in failed:', error);
      setError(error.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCognitoSignUp = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const result = await signUp({
        username: formData.username,
        password: formData.password,
        attributes: {
          email: formData.email,
          given_name: formData.firstName,
          family_name: formData.lastName
        }
      });

      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setLoginMode('cognito-confirm');
        setMessage('Please check your email for the confirmation code.');
      }
    } catch (error) {
      console.error('Cognito sign up failed:', error);
      setError(error.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      await confirmSignUp({
        username: formData.username,
        confirmationCode: formData.confirmationCode
      });

      setMessage('Account confirmed successfully! You can now sign in.');
      setLoginMode('cognito-signin');
    } catch (error) {
      console.error('Confirmation failed:', error);
      setError(error.message || 'Confirmation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await resendSignUpCode({ username: formData.username });
      setMessage('Confirmation code resent to your email.');
    } catch (error) {
      console.error('Resend code failed:', error);
      setError(error.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  if (loginMode === 'selection') {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="row w-100">
          <div className="col-md-6 col-lg-4 mx-auto">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-2">ResolveGuru</h2>
                  <p className="text-muted">Choose your login method</p>
                </div>

                <div className="d-grid gap-3">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleSAMLLogin}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Redirecting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-microsoft me-2"></i>
                        Sign in with KONE Azure AD
                      </>
                    )}
                  </button>

                  <div className="text-center my-3">
                    <span className="text-muted">or</span>
                  </div>

                  <button
                    className="btn btn-outline-primary btn-lg"
                    onClick={() => setLoginMode('cognito-signin')}
                    disabled={loading}
                  >
                    <i className="bi bi-person-circle me-2"></i>
                    Sign in with Cognito
                  </button>

                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setLoginMode('cognito-signup')}
                    disabled={loading}
                  >
                    Create Cognito Account
                  </button>
                </div>

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-signin') {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="row w-100">
          <div className="col-md-6 col-lg-4 mx-auto">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-2">Sign In</h2>
                  <p className="text-muted">Enter your Cognito credentials</p>
                </div>

                <form onSubmit={handleCognitoSignIn}>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setLoginMode('selection')}
                      disabled={loading}
                    >
                      Back
                    </button>
                  </div>
                </form>

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="alert alert-success mt-3" role="alert">
                    {message}
                  </div>
                )}

                <div className="text-center mt-3">
                  <small className="text-muted">
                    Don't have an account?{' '}
                    <button
                      className="btn btn-link p-0"
                      onClick={() => setLoginMode('cognito-signup')}
                    >
                      Sign up
                    </button>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-signup') {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="row w-100">
          <div className="col-md-6 col-lg-4 mx-auto">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-2">Create Account</h2>
                  <p className="text-muted">Sign up for a new Cognito account</p>
                </div>

                <form onSubmit={handleCognitoSignUp}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <small className="form-text text-muted">
                      Must be at least 8 characters with uppercase, lowercase, number, and symbol
                    </small>
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Confirm Password</label>
                    <input
                      type="password"
                      className="form-control"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setLoginMode('selection')}
                      disabled={loading}
                    >
                      Back
                    </button>
                  </div>
                </form>

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}

                <div className="text-center mt-3">
                  <small className="text-muted">
                    Already have an account?{' '}
                    <button
                      className="btn btn-link p-0"
                      onClick={() => setLoginMode('cognito-signin')}
                    >
                      Sign in
                    </button>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-confirm') {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="row w-100">
          <div className="col-md-6 col-lg-4 mx-auto">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-2">Confirm Account</h2>
                  <p className="text-muted">Enter the confirmation code sent to your email</p>
                </div>

                <form onSubmit={handleConfirmSignUp}>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.username}
                      disabled
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Confirmation Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="confirmationCode"
                      value={formData.confirmationCode}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter 6-digit code"
                    />
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Confirming...
                        </>
                      ) : (
                        'Confirm Account'
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleResendCode}
                      disabled={loading}
                    >
                      Resend Code
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setLoginMode('selection')}
                      disabled={loading}
                    >
                      Back to Login
                    </button>
                  </div>
                </form>

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="alert alert-success mt-3" role="alert">
                    {message}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default LoginSelection;